import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { LeaveBalanceTable } from '@/components/Approval/@components/AnnualLeave';
import { ApprovalFormTable } from '@/components/Approval/@components/ApprovalFormTable';
import {
  APPROVAL_SECTION_GAP_LG,
  InfoSectionTitle,
} from '@/components/Approval/@components/InfoSection';
import { ResignationEmployeeTable } from '@/components/Approval/@components/Resignation';
import { useFindPassport, useGetVacationStatus } from '@/hooks/apiHooks';
import useApprovalView from '@/hooks/approval/useApprovalView';
import {
  LEAVE_SUBTYPE_LABELS,
  LEAVE_UNIT_LABELS,
  leaveBalanceYearOf,
  parseAnnualLeaveRequestAttributes,
  type LeaveBalance,
} from '@/utils/approval/annualLeaveRequest';
import { normalizeLang, pickI18nText } from '@/utils/approval/i18nText';
import { formatAsDate } from '@/utils/format';
import type { VacationStatus } from '@/types/api';
import type { ContentExtraProps } from '../../types';
import { ContentSlotRoot } from '../styles';

const toBalance = (status: VacationStatus): LeaveBalance => ({
  monthlyLeave: status.monthlyLeave,
  annualLeave: status.annualLeave,
  overage: status.overage,
  remaining:
    status.monthlyLeave.granted +
    status.annualLeave.granted -
    (status.monthlyLeave.used + status.annualLeave.used + status.overage),
});

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * 32 연차휴가 — 레거시 appr_view_forms_year_leave_v2.
 * 휴근자 정보·휴가 사용 현황은 레거시처럼 작성자의 지금 값(현황은 휴가 시작일 연도), 신청 내용은 저장값
 */
const AnnualLeaveRequestSlot = ({ data }: ContentExtraProps) => {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.language);
  const { data: view } = useApprovalView(data.documentId);
  const author = view?.author;
  const passportId = author?.passportId ?? '';
  const { data: passport } = useFindPassport(passportId);
  const saved = parseAnnualLeaveRequestAttributes(data.attributes);
  const year = saved ? leaveBalanceYearOf(saved.startsAt) : undefined;
  const { data: vacation, error: vacationError } = useGetVacationStatus(
    passportId,
    year,
    { revalidateOnFocus: false },
  );
  if (!saved) return null;

  const unitLabel = (unit: keyof typeof LEAVE_UNIT_LABELS | null) =>
    unit ? `(${t(LEAVE_UNIT_LABELS[unit])})` : '';
  const workTime =
    saved.workStartHour !== null && saved.workEndHour !== null
      ? t('{{range}}시', {
          range: `${pad2(saved.workStartHour)}~${pad2(saved.workEndHour)}`,
        })
      : '';

  return (
    <ContentSlotRoot>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${APPROVAL_SECTION_GAP_LG}px`,
        }}
      >
        <Box>
          <InfoSectionTitle sx={{ mb: 1 }}>{t('휴근자 정보')}</InfoSectionTitle>
          <ResignationEmployeeTable
            employee={{
              name: author ? pickI18nText(author.name, lang) : '',
              employeeNumber:
                saved.employeeNumber || author?.employeeNumber || '',
              affiliation: author
                ? [
                    pickI18nText(author.companyName, lang),
                    pickI18nText(author.divisionName, lang),
                    author.departmentName,
                  ]
                    .filter(Boolean)
                    .join(' ')
                : '',
              enterDate: passport?.detail?.joinedAt
                ? formatAsDate(passport.detail.joinedAt)
                : '',
            }}
          />
        </Box>
        <Box>
          <InfoSectionTitle sx={{ mb: 1 }}>
            {t('휴가 사용 현황')}
          </InfoSectionTitle>
          {vacation ? (
            <LeaveBalanceTable balance={toBalance(vacation)} />
          ) : (
            <Box sx={{ fontSize: 14 }}>
              {vacationError ? t('데이터를 불러오지 못했습니다.') : '-'}
            </Box>
          )}
        </Box>
        <ApprovalFormTable>
          <tbody>
            <tr>
              <th>{t('근무시간')}</th>
              <th>{t('휴가 종류')}</th>
              <th>{t('휴가 기간')}</th>
            </tr>
            <tr>
              <td className="text-center">{workTime}</td>
              <td className="text-center">
                {saved.leaveSubtype
                  ? t(LEAVE_SUBTYPE_LABELS[saved.leaveSubtype])
                  : ''}
              </td>
              <td className="text-center">
                {`${saved.startsAt} ${unitLabel(saved.startsUnit)} ~ ${
                  saved.endsAt
                } ${unitLabel(saved.endsUnit)}`}
              </td>
            </tr>
          </tbody>
        </ApprovalFormTable>
      </Box>
    </ContentSlotRoot>
  );
};

export default AnnualLeaveRequestSlot;
